/**
 * RoadmapCommentLikeButton
 *
 * Heart-with-bubbles toggle for a roadmap ("Ideas") comment. Structural sibling
 * of the content CommentLikeButton, but bound to roadmap like-state via
 * useRoadmapCommentLike (ROADMAP_COMMENT_LIKES storage key).
 *
 * Per Flutter (roadmap_comment_widget.dart:446) only PARENT comments get a like
 * control; the caller (RoadmapCommentRow) is responsible for only rendering this
 * for root comments, and RoadmapCommentReplyRow renders no like button at all.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AnimatedBubblesButton } from '@/presentation/feature/content/components/AnimatedBubblesButton';
import { useRoadmapCommentLike } from '@/presentation/feature/roadmap/hooks/useRoadmapCommentLike';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface RoadmapCommentLikeButtonProps {
  commentId: string;
  likes: number;
  size: number;
  onToggle: (liked: boolean) => void;
}

export const RoadmapCommentLikeButton: React.FC<RoadmapCommentLikeButtonProps> = ({
  commentId,
  likes,
  size,
  onToggle,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { isLiked } = useRoadmapCommentLike(commentId);

  return (
    <div
      {...qaAttr({
        id: `roadmap-comment-like-${commentId}`,
        status: isLiked ? 'liked' : 'unliked',
      })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: responsive.gap.xs,
      }}
    >
      <AnimatedBubblesButton
        contentId={commentId}
        isLiked={isLiked}
        size={size}
        likeColor={theme.activeRed}
        unlikeColor={theme.icon.primary}
        onTap={() => onToggle(!isLiked)}
        animationDuration={ANIMATION_CONSTANTS.duration.long}
        circleColor={theme.exclusive}
        iconUrl="/assets/images/heart_fill.png"
        bubbles={[
          { color: theme.activeRed },
          { color: theme.blue },
          { color: theme.exclusive },
          { color: theme.exclusive },
          { color: theme.blue },
          { color: theme.exclusive },
        ]}
      />
      <span style={{ fontSize: responsive.font.xs, color: theme.text.secondary }}>
        {MathUtils.getReactionsIntlNumber(likes)}
      </span>
    </div>
  );
};
